// Utility functions for processing HTML content for rich text applications

// Function to convert Tailwind CSS classes to semantic HTML tags for rich text compatibility
export const convertCssClassesToSemanticHtml = (element: HTMLElement): void => {
  const processElement = (el: Element) => {
    const classes = el.className || '';
    
    // Convert font-bold to <strong> tag
    if (classes.includes('font-bold')) {
      if (el.tagName.toLowerCase() === 'span') {
        // Replace span with strong tag
        const strong = document.createElement('strong');
        strong.innerHTML = el.innerHTML;
        // Copy any other attributes except class
        Array.from(el.attributes).forEach(attr => {
          if (attr.name !== 'class') {
            strong.setAttribute(attr.name, attr.value);
          }
        });
        el.parentNode?.replaceChild(strong, el);
        return strong;
      } else {
        // Add inline style for non-span elements
        (el as HTMLElement).style.fontWeight = 'bold';
      }
    }
    
    // Convert font-italic or italic to <em> tag
    if (classes.includes('font-italic') || classes.includes('italic')) {
      if (el.tagName.toLowerCase() === 'span') {
        const em = document.createElement('em');
        em.innerHTML = el.innerHTML;
        Array.from(el.attributes).forEach(attr => {
          if (attr.name !== 'class') {
            em.setAttribute(attr.name, attr.value);
          }
        });
        el.parentNode?.replaceChild(em, el);
        return em;
      } else {
        (el as HTMLElement).style.fontStyle = 'italic';
      }
    }
    
    // Convert underline classes
    if (classes.includes('underline')) {
      (el as HTMLElement).style.textDecoration = 'underline';
    }
    
    return el;
  };
  
  // Process all elements with font-bold, font-italic, etc.
  const elementsToProcess = element.querySelectorAll('[class*="font-bold"], [class*="font-italic"], [class*="italic"], [class*="underline"]');
  
  // Convert to array to avoid live NodeList issues when replacing elements
  Array.from(elementsToProcess).forEach(el => {
    const newEl = processElement(el);
    // Process children of the new element if it was replaced
    if (newEl && newEl !== el) {
      const childElements = newEl.querySelectorAll('[class*="font-bold"], [class*="font-italic"], [class*="italic"], [class*="underline"]');
      Array.from(childElements).forEach(processElement);
    }
  });
};

// Function to find message element by messageId with fallback logic
export const findMessageElement = (messageId: string, componentName: string = 'Component'): HTMLElement | null => {
  console.log(`${componentName}: Starting rich text copy for messageId:`, messageId);
  
  // Try to find the message element by data attribute
  let messageContainer = document.querySelector(
    `[data-message-id="${messageId}"]`
  ) as HTMLElement | null;
  
  console.log(`${componentName}: messageContainer for ID`, messageId, messageContainer);

  let messageElement: HTMLElement | null = null;

  // If we found the container, look for the message-content inside it
  if (messageContainer) {
    messageElement = messageContainer.querySelector('.message-content') as HTMLElement | null;
    console.log(`${componentName}: messageElement inside container`, messageElement);
  }

  // Fallback: get the most recent message-content element
  if (!messageElement) {
    console.log(`${componentName}: falling back to last message-content`);
    const messageElements = document.querySelectorAll('.message-content');
    messageElement = messageElements[messageElements.length - 1] as HTMLElement;
    console.log(`${componentName}: fallback messageElement`, messageElement);
  }

  return messageElement;
};

// Function to process message element content for rich text copying
export const processMessageElementForRichText = (
  messageElement: HTMLElement, 
  componentName: string = 'Component'
): string => {
  console.log(`${componentName}: Found messageElement, processing content`);
  
  // Clone the element to avoid modifying the original
  const clonedElement = messageElement.cloneNode(true) as HTMLElement;
  
  // Remove sources section and other unwanted elements
  const sourcesToRemove = clonedElement.querySelectorAll(
    '[class*="sources"], [class*="Sources"], .sources-accordion, [data-testid*="sources"]'
  );
  console.log(`${componentName}: Removing`, sourcesToRemove.length, 'source elements');
  sourcesToRemove.forEach(source => source.remove());
  
  // Also remove any elements containing "Sources (" text
  const allElements = clonedElement.querySelectorAll('*');
  allElements.forEach(el => {
    if (el.textContent?.includes('Sources (')) {
      el.remove();
    }
  });
  
  // Convert Tailwind CSS classes to semantic HTML/inline styles for rich text compatibility
  const boldElementsBefore = clonedElement.querySelectorAll('[class*="font-bold"]').length;
  convertCssClassesToSemanticHtml(clonedElement);
  const boldElementsAfter = clonedElement.querySelectorAll('[class*="font-bold"]').length;
  const strongElements = clonedElement.querySelectorAll('strong').length;
  console.log(`${componentName}: Converted ${boldElementsBefore - boldElementsAfter} font-bold elements to ${strongElements} strong elements`);
  
  // Build minimal HTML doc wrapper to preserve bold/links/etc.
  const htmlContent = `
    <html>
      <body>
        ${clonedElement.innerHTML}
      </body>
    </html>
  `;
  
  console.log(`${componentName}: HTML content length:`, htmlContent.length);
  console.log(`${componentName}: HTML preview:`, htmlContent.substring(0, 200) + '...');
  
  return htmlContent;
};